/* crumb-vs-supernormal.js — vanilla progressive enhancement for the
 * Crumb vs Supernormal comparison page. Replaces the React runtime from the
 * prototype with one self-contained behaviour:
 *   1. the CompareTable widget — the "What matters most?" selector that
 *      highlights relevant rows + swaps the tailored verdict (1 useState).
 * No dependencies. Self-contained. Safe to load with `defer`.
 *
 * The FAQ accordion is owned by the shared converted/_assets/sn-accordion.js;
 * its answers are rendered statically into body.html (for SEO/crawlability),
 * so this file no longer touches the FAQ at all.
 *
 * The page renders a faithful static snapshot server-side (default `all`
 * state); this script enhances it in place — it MUTATES the existing SSR DOM
 * rather than rebuilding it, so the markup stays byte-faithful to body.html.
 * If the script never runs, the static table + verdict are still visible.
 */
(function () {
  'use strict';

  // ---- CompareTable data (ported verbatim from CompareTable.jsx) -----------
  var CMP_MATTERS = [
    { id: 'all',    label: 'Everything' },
    { id: 'home',   label: 'Getting them home' },
    { id: 'health', label: 'Health & care' },
    { id: 'price',  label: 'Lowest price' },
  ];

  var CMP_VERDICT = {
    all:    "For most owners who want the most from one tag, Supernormal edges it - AI vet and trainer, reminders. But Crumb’s biometric Snout ID is genuinely unique, so if that’s your priority, it’s the one to beat.",
    home:   "Close call. Both scan with no app and ping a local network the moment your pet’s missing. Crumb adds biometric Snout ID for if the tag’s removed.",
    health: "This is where Supernormal pulls ahead: Nova’s 24/7 AI vet, an AI trainer, and care reminders. Crumb offers human vet-nurse chat on Premium - useful - but no trainer or reminders.",
    price:  "Both start with a free tag. Crumb Premium is £2.95/mo; Supernormal Care is £4.99/mo after a free first month, with Nova, a trainer and reminders included. Pick on features, not pennies.",
  };

  // Only the row labels + their cats are needed to recompute styling; the
  // tick/cross cells already render correctly in body.html and never change.
  var CMP_ROWS = [
    { label: 'QR code scanning',          cats: ['home'] },
    { label: 'Location shared on scan',   cats: ['home'] },
    { label: 'Full editable profile',     cats: ['home', 'health'] },
    { label: 'Community lost-pet alerts', cats: ['home'] },
    { label: 'Biometric Snout ID',        cats: ['home'] },
    { label: 'Auto missing posters',      cats: ['home'] },
    { label: 'Vet advice',                cats: ['health'] },
    { label: 'AI trainer',                cats: ['health'] },
    { label: 'Care reminders',            cats: ['health'] },
    { label: 'No charging or battery',    cats: ['home'] },
    { label: 'Tag works free without a plan', cats: ['price'] },
    { label: 'Monthly price',             cats: ['price'] },
  ];

  function initCompareTable(root) {
    var matters = 'all';

    // The selector lives in the first flex container; its buttons are the
    // only buttons in the widget. They render in CMP_MATTERS order.
    var selectorWrap = root.querySelector('div > div');
    var btns = Array.prototype.slice.call(root.querySelectorAll('button'));

    // The table card is the sibling of the selector row (2nd child of inner div).
    var inner = root.firstElementChild;            // the React <div> wrapper
    var card = inner.children[1];                  // table card
    var verdictBox = inner.children[2];            // .sn-msg-in verdict
    var verdictP = verdictBox.querySelector('p');

    // Row elements: every direct child of the card after the header row.
    var rowEls = Array.prototype.slice.call(card.children).slice(1);

    function active(catsRow) {
      return matters === 'all' || catsRow.indexOf(matters) !== -1;
    }

    function syncButtons() {
      btns.forEach(function (btn, i) {
        var on = CMP_MATTERS[i].id === matters;
        btn.style.border = on ? '2px solid var(--sn-forest)' : '1.5px solid var(--sn-line)';
        btn.style.background = on ? 'var(--sn-forest)' : '#fff';
        btn.style.color = on ? 'var(--sn-cream)' : 'var(--sn-ink)';
      });
    }

    function syncRows() {
      rowEls.forEach(function (rowEl, i) {
        var row = CMP_ROWS[i];
        if (!row) return;
        var on = active(row.cats);
        var emphasise = on && matters !== 'all';
        rowEl.style.opacity = on ? '1' : '0.4';
        rowEl.style.background = emphasise ? 'rgba(207,255,144,0.12)' : 'transparent';
        var labelCell = rowEl.firstElementChild;
        labelCell.style.fontWeight = emphasise ? '700' : '500';
        labelCell.style.fontFamily = emphasise ? 'var(--sn-display)' : 'var(--sn-body)';
      });
    }

    function syncVerdict() {
      verdictP.textContent = CMP_VERDICT[matters];
      // Re-trigger the entrance animation (React remounts via `key={matters}`).
      verdictBox.classList.remove('sn-msg-in');
      void verdictBox.offsetWidth;
      verdictBox.classList.add('sn-msg-in');
    }

    btns.forEach(function (btn, i) {
      btn.addEventListener('click', function () {
        var id = CMP_MATTERS[i].id;
        if (id === matters) return;
        matters = id;
        syncButtons();
        syncRows();
        syncVerdict();
      });
    });

    // Initial state already rendered SSR; nothing to do on boot. Reference
    // selectorWrap so linters don't flag it; it's the anchor for the buttons.
    void selectorWrap;
  }

  // The FAQ accordion is handled by the shared converted/_assets/sn-accordion.js.
  // Answers are rendered into the static body.html (for SEO/crawlability), so this
  // file only owns the CompareTable widget.

  // ---- Boot -----------------------------------------------------------------
  function boot() {
    var page = document.getElementById('sn-crumb-vs-supernormal');
    if (!page) return;
    var widget = page.querySelector('[data-sn-widget="CompareTable"]');
    if (widget) initCompareTable(widget);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
